import { useRootFontSize } from "@ui/hooks/useRootFontSize";
import { pxToRem } from "@ui/lib/utils";
import { clsx } from "clsx";
import Image from "next/image";
import { type Placement, type PlacementAxis } from "react-aria";
import {
  Popover,
  ListBox as RACListBox,
  ListBoxItem,
  ListBoxSection,
  Header,
  Collection,
  type PopoverProps,
  Menu,
  MenuItem,
  MenuSection,
  Virtualizer,
  ListLayout,
} from "react-aria-components";

function getMaskImage({ placement, isEntering }: { placement: PlacementAxis | null; isEntering: boolean }) {
  const maskSize = "100% 300%";
  if (placement === "top") {
    return {
      maskSize,
      maskImage: "linear-gradient(to top, white 33.333333%, transparent 66.666666%)",
      maskPosition: isEntering ? "0% 0%" : "0% 100%",
    };
  }
  return {
    maskSize,
    maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
    maskPosition: isEntering ? "0% 100%" : "0% 0%",
  };
}

export interface ItemBase {
  id: string;
  textValue: string;
  label?: string;
  subLabel?: string;
  image?: Omit<React.ComponentPropsWithoutRef<typeof Image>, "sizes" | "className" | "style" | "alt">;
  link?: { href: string; target?: React.HTMLAttributeAnchorTarget };
  action?: () => void;
}

export interface SectionBase<Item extends ItemBase = ItemBase> {
  id: string;
  label: string;
  items: Item[];
}

type ListBoxPropsBase<Item extends ItemBase> = {
  children?: (item: Item) => React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  placement?: Placement;
  asMenu?: boolean;
  selectionMode?: "single" | "multiple" | "none";
  "aria-label"?: string;
  virtualize?: boolean;
  scrollTo?: (options: { rowHeight: number }) => number;
  renderEmptyState?: () => React.ReactNode;
} & Pick<PopoverProps, "offset" | "crossOffset" | "isOpen" | "onOpenChange" | "triggerRef">;

type ListBoxPropsWithItems<Item extends ItemBase> = ListBoxPropsBase<Item> & {
  /** Flat list of items */
  items: Array<Item>;
  /** Cannot use sections when items is provided */
  sections?: never;
};

type ListBoxPropsWithSections<Item extends ItemBase> = ListBoxPropsBase<Item> & {
  /** Cannot use items when sections is provided */
  items?: never;
  /** Grouped items with section headers */
  sections: Array<SectionBase<Item>>;
};

type ListBoxProps<Item extends ItemBase> = ListBoxPropsWithItems<Item> | ListBoxPropsWithSections<Item>;

function defaultRenderItem(item: ItemBase) {
  return <DefaultItem item={item} />;
}

export function DefaultItem({
  item,
  showImage = true,
  showLabel = true,
  showSubLabel = true,
  labelClassName,
  imageClassName,
}: {
  item: ItemBase;
  showImage?: boolean;
  showLabel?: boolean;
  showSubLabel?: boolean;
  labelClassName?: string;
  imageClassName?: string;
}) {
  const label = item.label || item.textValue;

  return (
    <div className='tw-relative tw-min-w-0 tw-flex tw-items-baseline tw-gap-2'>
      {showImage && item.image && (
        <Image
          sizes='1rem'
          className={clsx(
            "tw-self-center tw-flex-none tw-size-4 tw-object-cover tw-rounded-full tw-bg-gray-200",
            imageClassName,
          )}
          alt=''
          {...item.image}
        />
      )}
      {showLabel && <div className={clsx("tw-truncate", labelClassName)}>{label}</div>}
      {showSubLabel && item.subLabel && <span className='tw-text-gray-500 tw-text-2xs tw-ml-1'>{item.subLabel}</span>}
    </div>
  );
}

export function ListBox<Item extends ItemBase>({
  items,
  sections,
  children: renderItem = defaultRenderItem,
  className,
  style,
  placement,
  offset = 8,
  crossOffset = 0,
  asMenu = false,
  selectionMode,
  "aria-label": ariaLabel,
  virtualize = false,
  scrollTo,
  renderEmptyState,
  ...props
}: ListBoxProps<Item>) {
  const { rootFontScale } = useRootFontSize();
  const Component = asMenu ? Menu : RACListBox;
  const ItemComponent = asMenu ? MenuItem : ListBoxItem;
  const SectionComponent = asMenu ? MenuSection : ListBoxSection;
  const rowHeight = 36 * rootFontScale;
  const showCheckmark = selectionMode !== "none";

  // Flatten items for calculating max height and item indices
  const allItems = sections ? sections.flatMap(s => s.items) : (items ?? []);

  const getItemClassName =
    (item: Item, isFirst: boolean, isLast: boolean) =>
    ({ isHovered, isFocusVisible, isSelected }: { isHovered: boolean; isFocusVisible: boolean; isSelected: boolean }) =>
      clsx(
        "tw-pl-2.5 tw-h-9 tw-flex tw-gap-2 tw-items-center tw-cursor-pointer tw-relative",
        "before:tw-absolute before:tw-bg-gray-50 before:tw-rounded-xl before:tw-inset-0",
        // Extend hit area to the edges of the list box
        "after:tw-absolute after:-tw-inset-x-1.5 after:tw-inset-y-0",
        isFirst && "after:-tw-top-1.5",
        isLast && "after:-tw-bottom-1.5",
        "tw-outline-none",
        !isHovered && !isFocusVisible && "before:tw-opacity-0 before:tw-scale-95",
        isFocusVisible && "before:tw-outline-focus",
        !isFocusVisible && "before:tw-transition",
        showCheckmark ? (isSelected ? "tw-pr-1.5" : "tw-pr-[2.375rem]") : "tw-pr-2.5",
      );

  const renderItemContent = (item: Item, isSelected: boolean) => (
    <>
      {renderItem(item)}
      {showCheckmark && isSelected && (
        <svg
          viewBox='0 0 24 24'
          fill='none'
          className='tw-relative tw-ml-auto tw-size-6 tw-flex-none tw-text-black'
          aria-hidden
        >
          <path
            d='M6.75 13.0625L9.9 16.25L17.25 7.75'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
    </>
  );

  const listbox = (
    <Component
      ref={node => {
        // Workaround for React Aria issue with scroll restoration when virtualized.
        if (
          typeof scrollTo === "function" &&
          node?.parentNode instanceof HTMLElement &&
          !node.parentNode.hasAttribute("data-exiting")
        ) {
          node.scrollTo({ top: scrollTo({ rowHeight }) });
        }
      }}
      selectionMode={selectionMode}
      aria-label={ariaLabel}
      renderEmptyState={renderEmptyState}
      className={clsx(
        "tw-flex-auto tw-text-sm/sm tw-text-black tw-outline-none tw-overflow-auto tw-scrollbar-none tw-scroll-py-1.5 tw-bg-white tw-rounded-2xl tw-pointer-events-auto tw-relative",
        !virtualize && "tw-p-1.5",
      )}
      style={{
        maxHeight: allItems.length > 6 ? pxToRem(206) : undefined,
        boxShadow: "0 0 1px 0 rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)",
      }}
    >
      {sections ? (
        <Collection items={sections}>
          {section => (
            <SectionComponent id={section.id}>
              <Header
                className={clsx(
                  "tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-500",
                  section !== sections[0] && "tw-mt-2",
                )}
              >
                {section.label}
              </Header>
              <Collection items={section.items}>
                {item => {
                  const isFirst = section === sections[0] && item === section.items[0];
                  const isLast =
                    section === sections[sections.length - 1] && item === section.items[section.items.length - 1];
                  return (
                    <ItemComponent
                      id={item.id}
                      textValue={item.textValue}
                      onAction={item.action}
                      href={item.link?.href}
                      target={item.link?.target}
                      className={getItemClassName(item, isFirst, isLast)}
                    >
                      {({ isSelected }: { isSelected: boolean }) => renderItemContent(item, isSelected)}
                    </ItemComponent>
                  );
                }}
              </Collection>
            </SectionComponent>
          )}
        </Collection>
      ) : (
        <Collection items={items ?? []}>
          {item => (
            <ItemComponent
              id={item.id}
              textValue={item.textValue}
              onAction={item.action}
              href={item.link?.href}
              target={item.link?.target}
              data-index={allItems.indexOf(item)}
              className={getItemClassName(item, item === allItems[0], item === allItems[allItems.length - 1])}
            >
              {({ isSelected }: { isSelected: boolean }) => renderItemContent(item, isSelected)}
            </ItemComponent>
          )}
        </Collection>
      )}
    </Component>
  );

  return (
    <Popover
      className={clsx(
        "tw-outline-none tw-flex tw-flex-col tw-p-4 -tw-m-4 tw-ease-in-out tw-pointer-events-none",
        className,
      )}
      placement={placement}
      offset={offset * rootFontScale}
      crossOffset={crossOffset * rootFontScale}
      style={({ isEntering, isExiting, placement }) => ({
        transitionDuration: isExiting ? "0.15s" : "0.3s",
        transitionProperty: "mask-position, opacity",
        opacity: isExiting ? 0 : 1,
        ...getMaskImage({ placement, isEntering }),
        ...style,
      })}
      {...props}
    >
      {virtualize ? (
        <Virtualizer layout={ListLayout} layoutOptions={{ rowHeight, padding: 6 * rootFontScale }}>
          {/*
          Overlay that sits at the top of the dropdown to block pointer events.
          This is a workaround for a React Aria issue with scroll restoration.
          */}
          <div className='tw-sticky tw-z-50 tw-h-1.5 tw-top-0 -tw-mb-1.5 tw-pointer-events-auto' />
          {listbox}
        </Virtualizer>
      ) : (
        listbox
      )}
    </Popover>
  );
}
